import { Children } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import xss from 'xss';

const BLOCK_TAGS = new Set([
  'DIV',
  'P',
  'UL',
  'OL',
  'LI',
  'TABLE',
  'THEAD',
  'TBODY',
  'TR',
  'TD',
  'TH',
  'BLOCKQUOTE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
]);

/**
 * HtmlService
 *
 * This service class provides core HTML support.
 */
class HtmlServiceCls {
  /**
   * Sanitise HTML.
   */
  sanitiseHTML(html) {
    return xss(html, {
      whiteList: {},
      stripIgnoreTag: true,
      // the script tag is a special case, we need to filter out its content
      // stripIgnoreTagBody: ['script'],
    });
  }

  /*
  |--------------------------------------------------------------------------
  | For "contenteditable" HTML.
  |--------------------------------------------------------------------------
  */

  /**
   * Pasted content (from elsewhere in this same report, Word, Google Docs)
   * commonly carries inline style="" attributes — in particular
   * font-size: var(--doc-body-size, 14px), which this report template itself
   * uses for body text. A paragraph split (Enter) that lands on the boundary
   * of a styled inline <span> confuses Chrome's caret placement: the browser
   * does insert a new paragraph (confirmed via the beforeinput/input
   * "insertParagraph" events actually firing, and a new element landing in
   * the DOM) but the caret doesn't move into it, so typing keeps appending to
   * the old paragraph and the new one sits empty and invisible. Stripping
   * inline styles removes the trigger; the template's own typography rules
   * apply regardless of these attributes anyway, so nothing is lost.
   */
  stripInlineStyles(root) {
    root
      .querySelectorAll('[style]')
      .forEach((el) => el.removeAttribute('style'));
  }

  /**
   * Some saved rich-text content (typically pasted from Word/Google Docs, or
   * produced by an external tool) mixes inline nodes — bare text, <span>,
   * <br> — directly alongside block-level siblings like <div> or <p> within
   * the same container, e.g. <div><span>Para one</span><span>Para two</span>
   * <div><br></div></div>. Chrome's contentEditable handles that fine while
   * editing in the middle (there's content on both sides to split), but
   * clicking at the end of the last inline run and pressing Enter silently
   * does nothing — there's no block boundary for it to split at that point.
   * This walks the tree and wraps every run of inline siblings that sits next
   * to a block-level sibling into its own <div>, so every reachable cursor
   * position has a real block to split. Containers that are purely inline
   * (no block children at all) are left alone — the browser handles those
   * fine since there's nothing after them to get stuck next to.
   */
  blockifyStrayInlineRuns(root) {
    const hasBlockChild = Array.from(root.children).some((el) =>
      BLOCK_TAGS.has(el.tagName)
    );
    if (!hasBlockChild) return;
    let run = null;
    Array.from(root.childNodes).forEach((node) => {
      const isBlock = node.nodeType === 1 && BLOCK_TAGS.has(node.tagName);
      if (isBlock) {
        run = null;
        this.blockifyStrayInlineRuns(node);
      } else {
        if (!run) {
          run = document.createElement('div');
          root.insertBefore(run, node);
        }
        run.appendChild(node);
      }
    });
  }

  /**
   * Reverse of the legacy-plain-text load path above (each "\n" line → its own
   * <div>, blank lines → <div><br></div>): walks the edited DOM and rejoins
   * each top-level block (and each <br>) back into a "\n"-separated plain
   * string, dropping every tag. Used by saveAsSimpleText so a field can get the
   * richText contentEditable UX (real <div> per line, so Enter/paste behave
   * like every other WriterNote) while still persisting tagless text for
   * legacy consumers that were never going to render HTML.
   */
  htmlToPlainText(root) {
    const lines = [];
    let current = '';
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        current += node.textContent;
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      if (node.tagName === 'BR') {
        lines.push(current);
        current = '';
        return;
      }
      const isBlock = BLOCK_TAGS.has(node.tagName);
      Array.from(node.childNodes).forEach(walk);
      // A block whose own last child is a <br> (the exact shape the load path
      // above produces for a blank line, <div><br></div>) already had that
      // line flushed by the BR branch above — flushing again here would double
      // up every blank line.
      const lastChild = node.lastChild;
      const endsInBr =
        lastChild && lastChild.nodeType === 1 && lastChild.tagName === 'BR';
      if (isBlock && !endsInBr) {
        lines.push(current);
        current = '';
      }
    };
    Array.from(root.childNodes).forEach(walk);
    if (current !== '') {
      lines.push(current);
    }
    return lines.join('\n');
  }

  /**
   * Apply normalisation processes to the given html string.
   *
   * Given text contains a real tag (already an HTML string, e.g. CKEditor output),
   */
  normalizeRichTextHtml(html) {
    const container = document.createElement('div');
    container.innerHTML = html;
    this.stripInlineStyles(container);
    this.blockifyStrayInlineRuns(container);
    return container.innerHTML;
  }

  /**
   * Sanitizes pasted content the same way — strips inline styles so newly
   * pasted text doesn't reintroduce the caret bug above — while preserving
   * basic formatting tags (bold, italic, lists, etc).
   */
  sanitizePastedHtml(html) {
    const container = document.createElement('div');
    container.innerHTML = html;
    this.stripInlineStyles(container);
    container
      .querySelectorAll('[class]')
      .forEach((el) => el.removeAttribute('class'));
    return container.innerHTML;
  }

  hasContent(children) {
    return Children.toArray(children).some(Boolean);
  }

  /**
   * Detection requires an actual tag (<letter ...>), not just a leading
   * "<" — real saved content almost always starts with plain text (the
   * browser only wraps the *second* paragraph onward in a <div> on
   * Enter), so a leading-character sniff misclassified real HTML as
   * plain text on every multi-paragraph field. That routed it through
   * renderToStaticMarkup, which escaped the embedded tags into visible
   * "&lt;div&gt;" text — and if the field was blurred again in that
   * corrupted state, the escaped entities got captured and saved,
   * compounding on every subsequent edit. Requiring a full tag pattern
   * (rather than trusting richText alone) also protects plain text that
   * happens to contain a bare "<", e.g. "Trash <--> Treasure".
   */
  htmlStringHasTags(html) {
    return typeof html === 'string' && /<[a-z][^>]*>/i.test(html);
  }

  /**
   * Legacy plain text saved before richText/innerHTML capture existed —
   * no real tags, just "\n" paragraph breaks. Wrap each line in its own
   * <div>, matching exactly what the browser's own contentEditable
   * produces when you type and press Enter. A flat run of text with
   * inline <br /> tags (the previous approach) looks fine but Chrome's
   * Enter-key handling is unreliable at the very end of such a run —
   * it can't tell where to insert a new paragraph when there's no block
   * element to split, so Enter silently does nothing there. Real <div>
   * elements don't have that edge case. Empty lines get a <br> inside
   * so they still render with visible height, same as Chrome's own
   * "<div><br></div>" for a blank line.
   */
  simpleStringToHtml(str) {
    return renderToStaticMarkup(<>{str}</>)
      .split(/\r\n|\r|\n/)
      .map((line) => `<div>${line?.trim() ? line : '<br>'}</div>`)
      .join('');
  }

  /**
   * Plain-text → paragraph-based HTML, for destinations (e.g. CKEditor) that
   * expect <p> rather than the <div>-per-line shape simpleStringToHtml
   * produces for contentEditable. Blank line(s) delimit paragraphs — one or
   * more consecutive blank lines all collapse to a single paragraph break,
   * matching how most editors treat paragraph spacing. Within a paragraph,
   * a hard return becomes a <br/>.
   */
  simpleStringToParaBasedHtml(str) {
    const lines = renderToStaticMarkup(<>{str}</>).split(/\r\n|\r|\n/);

    const paragraphs = [];
    let current = [];
    lines.forEach((line) => {
      if (line.trim() === '') {
        // Blank line: close off the current paragraph, if one is open.
        // A run of several blank lines in a row hits this branch repeatedly
        // with current already empty, so it's a no-op after the first —
        // that's what collapses multiple blank lines into one break.
        if (current.length > 0) {
          paragraphs.push(current.join('<br/>'));
          current = [];
        }
        return;
      }
      current.push(line);
    });
    if (current.length > 0) {
      paragraphs.push(current.join('<br/>'));
    }

    return paragraphs.map((p) => `<p>${p}</p>`).join('');
  }

  /**
   * Turn jsx/html/plain string into innerHtml
   */
  convertToInnerHtml({
    children,
    richText = true,
    htmlConversionStyle = 'para',
    returnAs = 'string',
  }) {
    let htmlString = '';
    const hasContent = this.hasContent(children);

    if (!hasContent) {
      htmlString = '';
    } else if (richText && this.htmlStringHasTags(children)) {
      // Contains a real tag — already an HTML string (e.g. CKEditor output,
      // or sProject.analysis/reflection/etc captured via
      // e.currentTarget.innerHTML on a previous save). Use directly —
      // renderToStaticMarkup would escape < > and corrupt the markup.
      htmlString = this.normalizeRichTextHtml(children);
    } else if (richText && typeof children === 'string') {
      // Legacy plain text saved before richText/innerHTML capture existed —
      // no real tags, just "\n" paragraph breaks. Wrap each line in its own
      // <div>, matching exactly what the browser's own contentEditable
      // produces when you type and press Enter. A flat run of text with
      // inline <br /> tags (the previous approach) looks fine but Chrome's
      // Enter-key handling is unreliable at the very end of such a run —
      // it can't tell where to insert a new paragraph when there's no block
      // element to split, so Enter silently does nothing there. Real <div>
      // elements don't have that edge case. Empty lines get a <br> inside
      // so they still render with visible height, same as Chrome's own
      // "<div><br></div>" for a blank line.

      htmlString =
        htmlConversionStyle === 'para'
          ? this.simpleStringToParaBasedHtml(children)
          : this.simpleStringToHtml(children);
    } else {
      htmlString = renderToStaticMarkup(<>{children}</>);
    }

    return returnAs === 'string' ? (
      htmlString
    ) : (
      <div dangerouslySetInnerHTML={{ __html: htmlString }} />
    );
  }
}

// service class is a singleton
const HtmlService = new HtmlServiceCls();

export { HtmlService };
